<template>
  <div v-if="visible" class="line-login">
    <!--
      Admin-authored HTML from the LINE ID settings screen (issue #1660
      section 2.1), never visitor input. Passed through LineInfo's
      sanitize_fields, following the Faq / Information convention.

      That is NOT an XSS control and must not be read as one. Its scrubber
      deliberately keeps <script src="{{ file_root_path }}...">, because
      admin-authored LP templates rely on it, and Rails ignores the tags /
      attributes allow-list entirely once a scrubber is supplied - so
      onerror, <iframe> and javascript: all survive. The control here is that
      only shop admins can write this field.
    -->
    <!-- eslint-disable vue/no-v-html -->
    <div
      v-if="lineInfo && lineInfo.sanitizeInformationContext"
      class="line-login-info"
      v-html="lineInfo.sanitizeInformationContext"
    ></div>
    <!-- eslint-enable vue/no-v-html -->

    <a class="line-login-btn" :href="loginPath">
      <img class="line-login-btn__icon" src="/assets/images/line/login-line.png" alt="" />
      <span class="line-login-btn__label">LINEでログイン</span>
    </a>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted } from "vue";
import { useLineInfoStore } from "@/stores/line_info";
import { useAuthStore } from "@/stores/auth";

interface Props {
  origin: string;
  urlId?: number;
  orderNumber?: string;
}

const props = defineProps<Props>();

const lineInfoStore = useLineInfoStore();
const authStore = useAuthStore();

const lineInfo = computed(() => lineInfoStore.lineInfo);

// D2: hide when a login session exists and that customer already has a
// LINE ID linked. Guests (no token) always fall through to the settings.
const lineLinked = computed(() => authStore.isLoggedIn && authStore.customer?.lineLinked === true);

// authStore.customer is not persisted, so it is null on every page load. Gating
// on "linked" alone would flash the button for a customer who is already linked
// - exactly what the D2 hide rule forbids - until customersSelf resolves.
const customerLoaded = computed(() => !authStore.isLoggedIn || authStore.customer !== null);

const visible = computed(
  () => customerLoaded.value && lineInfo.value?.displayBtn === true && !lineLinked.value,
);

const loginPath = computed(() => {
  const params = new URLSearchParams({ origin: props.origin });
  if (props.urlId != null) params.set("url_id", String(props.urlId));
  if (props.orderNumber) params.set("order_number", props.orderNumber);
  return `/line/login?${params.toString()}`;
});

onMounted(() => {
  // Both are best-effort: a failure here must not pop an error toast over the
  // purchase form (the LP's own fetchCustomer passes errorToasted: false for
  // the same reason) and must not leave an unhandled rejection.
  if (!lineInfoStore.lineInfo) lineInfoStore.fetchLineInfo().catch(() => undefined);
  if (authStore.isLoggedIn && !authStore.customer) {
    authStore.fetchCustomerInfo().catch(() => undefined);
  }
});
</script>

<style lang="scss" scoped>
// Construction taken from the reference button (fill #2FA434, square icon cell
// flush to the left edge, 1px #66B84D rule, label centred in the space that
// rule leaves - not in the button as a whole). The SIZE, however, is taken
// from the screen it sits on: every surface that renders this button also
// renders a `b-button size="lg"` primary action, and that measures 300x70 with
// an 8px radius and a ~15px label on both the sign-in screen and the LP. Width,
// radius and label size are mirrored below; the height is set to 55 on request
// so the two read as a pair without competing.
//
// Consequence of keeping the icon cell square while the button got much
// shorter in aspect: the logo now occupies 55/300 of the width where the
// reference gave it 36/273. That is the cost of sizing against the neighbour
// instead of the reference; the alternative was a much wider button.
//
// NOTE — this is the ecforce button, and it is NOT what LINE's own design
// guidelines specify (those call for #06C755 with the LINE icon intact and a
// documented isolation zone). Matching the reference was the instruction, so
// the deviation is deliberate; revisit if a brand review ever asks for the
// official asset kit.
.line-login {
  // The block now sits at the very top of the LP, where a 16px margin read as
  // flush against the viewport edge.
  margin: 32px 0;

  // Flex column purely so the info/button spacing comes from `gap`. A margin
  // cannot be trusted here: the copy above is admin-authored HTML from the
  // LINE ID settings screen, the stock ecforce template ends on a div with
  // `margin-bottom: -15px`, and that collapsed a 20px margin down to 5px. gap
  // is applied between the items' margin boxes, so no amount of negative
  // margin inside the admin HTML can eat it.
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.line-login-info {
  font-size: 13px;
  text-align: left;
}

.line-login-btn {
  display: flex;
  align-items: stretch;
  width: 100%;
  // 300 wide and an 8px radius come from the `b-button size="lg"` primary
  // action next to it; the height is deliberately 55 rather than that button's
  // 70, so the LINE option reads as the secondary of the two. Hard-coded
  // rather than reusing the theme's .btn-lg, because .btn-lg gets its height
  // from padding and padding would break the flush icon cell.
  max-width: 300px;
  height: 55px;
  margin: 0 auto;
  background-color: #2fa434;
  border-radius: 8px;
  overflow: hidden;
  text-decoration: none;

  &__icon {
    flex: 0 0 55px;
    width: 55px;
    height: 55px;
    display: block;
  }

  // The rule lives on the label so it sits hard against the icon cell and
  // spans the full height, exactly as measured.
  &__label {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    border-left: 1px solid #66b84d;
    color: #ffffff;
    // 15px to match the neighbouring primary button, which computes to
    // 14.95px from the theme's .btn-lg.
    font-size: 15px;
    font-weight: 700;
    line-height: 1;
  }

  &:hover &__label {
    color: #ffffff;
  }

  &:hover {
    background-color: #2a9330;
  }
}
</style>
